/** Parse user_emails setting (newline- and/or comma-separated addresses). */
export function parseConfiguredInboxes(raw: string): string[] {
  if (!raw?.trim()) return []
  const seen = new Set<string>()
  const out: string[] = []
  for (const line of raw.replace(/\r/g, '\n').split('\n')) {
    for (const part of line.split(',')) {
      const email = part.trim()
      if (email && email.includes('@') && !seen.has(email)) {
        seen.add(email)
        out.push(email)
      }
    }
  }
  return out
}

export function inboxLocalPart(email: string): string | null {
  const trimmed = email.trim()
  const at = trimmed.indexOf('@')
  if (at <= 0) return null
  return trimmed.slice(0, at)
}

/** Unique configured inbox for a shorthand or full-address token. */
export function resolveInboxToken(token: string, configured: string[]): string | null {
  const trimmed = token.trim()
  if (!trimmed) return null

  const tokenLower = trimmed.toLowerCase()
  const byLower = new Map(configured.map((email) => [email.toLowerCase(), email]))
  if (byLower.has(tokenLower)) return byLower.get(tokenLower)!

  if (trimmed.includes('@')) return null

  const exactLocal = configured.filter(
    (email) => inboxLocalPart(email)?.toLowerCase() === tokenLower,
  )
  if (exactLocal.length === 1) return exactLocal[0] ?? null

  const prefixLocal = configured.filter((email) => {
    const local = inboxLocalPart(email)?.toLowerCase()
    return local != null && local.startsWith(tokenLower)
  })
  if (prefixLocal.length === 1) return prefixLocal[0] ?? null

  return null
}

export function completeInboxFilterTokens(value: string, configured: string[]): string {
  if (!value.trim() || configured.length === 0) return value

  return value
    .split(',')
    .map((part) => {
      const trimmed = part.trim()
      if (!trimmed) return part
      const resolved = resolveInboxToken(trimmed, configured)
      if (!resolved) return part
      const leading = part.match(/^\s*/)?.[0] ?? ''
      return `${leading}${resolved}`
    })
    .join(',')
}

export function getCurrentInboxSegment(
  value: string,
  cursorPos: number,
): { start: number; end: number; token: string } {
  const before = value.slice(0, cursorPos)
  const after = value.slice(cursorPos)
  const commaBefore = before.lastIndexOf(',')
  const commaAfter = after.indexOf(',')
  const segmentStart = commaBefore === -1 ? 0 : commaBefore + 1
  const segmentEnd = commaAfter === -1 ? value.length : cursorPos + commaAfter
  const segment = value.slice(segmentStart, segmentEnd)
  const token = segment.trim()
  const leadPad = segment.match(/^\s*/)?.[0]?.length ?? 0
  const trailPad = segment.match(/\s*$/)?.[0]?.length ?? 0
  return {
    start: segmentStart + leadPad,
    end: segmentEnd - trailPad,
    token,
  }
}

export function getInboxInlineSuggestion(
  value: string,
  cursorPos: number,
  configured: string[],
): { prefixBeforeGhost: string; ghostSuffix: string } | null {
  const { start, token } = getCurrentInboxSegment(value, cursorPos)
  if (!token || token.includes('@')) return null

  const resolved = resolveInboxToken(token, configured)
  if (!resolved) return null

  const typedUpToCursor = value.slice(start, cursorPos)
  const resolvedLocal = inboxLocalPart(resolved)
  if (!resolvedLocal?.toLowerCase().startsWith(typedUpToCursor.toLowerCase())) return null

  const ghostSuffix = resolved.slice(typedUpToCursor.length)
  if (!ghostSuffix) return null

  return {
    prefixBeforeGhost: value.slice(0, start) + typedUpToCursor,
    ghostSuffix,
  }
}

export function completeInboxAtCursor(
  value: string,
  cursorPos: number,
  configured: string[],
): { value: string; cursorPos: number } | null {
  const { start, end, token } = getCurrentInboxSegment(value, cursorPos)
  if (!token) return null

  const resolved = resolveInboxToken(token, configured)
  if (!resolved) return null

  return {
    value: value.slice(0, start) + resolved + value.slice(end),
    cursorPos: start + resolved.length,
  }
}
